// src/broadcast/stores/useBroadcastStore.ts
"use client"

import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { enableMapSet } from 'immer'
import type { MessageRecord } from '../types/events/message'

// Enable Map/Set support for Immer
enableMapSet()

/**
 * Connection state
 */
interface ConnectionState {
  isConnected: boolean
  connectionError: Error | null
  lastEventTimestamp: number | null
  reconnectAttempts: number
}

/**
 * Message state
 */
interface MessageState {
  /** Messages indexed by message_id */
  messages: Map<string, MessageRecord>

  /** Messages by conversation for quick lookup */
  messagesByConversation: Map<string, Set<string>>

  /** Pending messages (optimistic updates) */
  pendingMessages: Map<string, MessageRecord>
}

/**
 * Store actions
 */
interface BroadcastActions {
  // Connection actions
  setConnected: (connected: boolean) => void
  setConnectionError: (error: Error | null) => void
  setLastEventTimestamp: (timestamp: number) => void
  incrementReconnectAttempts: () => void
  resetReconnectAttempts: () => void

  // Message actions
  addMessage: (message: MessageRecord) => void
  updateMessage: (messageId: string, updates: Partial<MessageRecord>) => void
  removeMessage: (messageId: string) => void

  // Bulk operations
  addMessages: (messages: MessageRecord[]) => void
  clearConversationMessages: (conversationId: string) => void

  // Optimistic updates
  addPendingMessage: (tempId: string, message: MessageRecord) => void
  confirmPendingMessage: (tempId: string, confirmedMessage: MessageRecord) => void
  removePendingMessage: (tempId: string) => void

  // Selectors (computed)
  getMessagesByConversation: (conversationId: string) => MessageRecord[]
  getMessage: (messageId: string) => MessageRecord | undefined

  // Reset
  reset: () => void
}

type BroadcastStore = ConnectionState & MessageState & BroadcastActions

const initialState: ConnectionState & MessageState = {
  // Connection
  isConnected: false,
  connectionError: null,
  lastEventTimestamp: null,
  reconnectAttempts: 0,

  // Messages
  messages: new Map(),
  messagesByConversation: new Map(),
  pendingMessages: new Map(),
}

export const useBroadcastStore = create<BroadcastStore>()(
  immer((set, get) => ({
    ...initialState,

    // Connection actions
    setConnected: (connected) => set((state) => {
      state.isConnected = connected
      if (connected) {
        state.connectionError = null
        state.reconnectAttempts = 0
      }
    }),

    setConnectionError: (error) => set((state) => {
      state.connectionError = error
      state.isConnected = false
    }),

    setLastEventTimestamp: (timestamp) => set((state) => {
      state.lastEventTimestamp = timestamp
    }),

    incrementReconnectAttempts: () => set((state) => {
      state.reconnectAttempts += 1
    }),

    resetReconnectAttempts: () => set((state) => {
      state.reconnectAttempts = 0
    }),

    // Message actions
    addMessage: (message) => set((state) => {
      const { message_id, conversation_id } = message

      // Add to main messages map
      state.messages.set(message_id, message)

      // Add to conversation index
      if (!state.messagesByConversation.has(conversation_id)) {
        state.messagesByConversation.set(conversation_id, new Set())
      }
      state.messagesByConversation.get(conversation_id)!.add(message_id)
    }),

    updateMessage: (messageId, updates) => set((state) => {
      const existing = state.messages.get(messageId)
      if (existing) {
        state.messages.set(messageId, { ...existing, ...updates })
      }
    }),

    removeMessage: (messageId) => set((state) => {
      const message = state.messages.get(messageId)
      if (message) {
        // Remove from conversation index
        const convMessages = state.messagesByConversation.get(message.conversation_id)
        if (convMessages) {
          convMessages.delete(messageId)
        }
        // Remove from main map
        state.messages.delete(messageId)
      }
    }),

    addMessages: (messages) => set((state) => {
      messages.forEach((message) => {
        const { message_id, conversation_id } = message
        state.messages.set(message_id, message)

        if (!state.messagesByConversation.has(conversation_id)) {
          state.messagesByConversation.set(conversation_id, new Set())
        }
        state.messagesByConversation.get(conversation_id)!.add(message_id)
      })
    }),

    clearConversationMessages: (conversationId) => set((state) => {
      const messageIds = state.messagesByConversation.get(conversationId)
      if (messageIds) {
        messageIds.forEach((id) => state.messages.delete(id))
        state.messagesByConversation.delete(conversationId)
      }
    }),

    // Optimistic updates
    addPendingMessage: (tempId, message) => set((state) => {
      state.pendingMessages.set(tempId, message)
    }),

    confirmPendingMessage: (tempId, confirmedMessage) => set((state) => {
      state.pendingMessages.delete(tempId)
      // Add the confirmed message
      const { message_id, conversation_id } = confirmedMessage
      state.messages.set(message_id, confirmedMessage)

      if (!state.messagesByConversation.has(conversation_id)) {
        state.messagesByConversation.set(conversation_id, new Set())
      }
      state.messagesByConversation.get(conversation_id)!.add(message_id)
    }),

    removePendingMessage: (tempId) => set((state) => {
      state.pendingMessages.delete(tempId)
    }),

    // Selectors
    getMessagesByConversation: (conversationId) => {
      const state = get()
      const messageIds = state.messagesByConversation.get(conversationId)
      if (!messageIds) return []

      const messages: MessageRecord[] = []
      messageIds.forEach((id) => {
        const msg = state.messages.get(id)
        if (msg) messages.push(msg)
      })

      // Sort by created_at
      return messages.sort((a, b) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      )
    },

    getMessage: (messageId) => {
      return get().messages.get(messageId)
    },

    // Reset
    reset: () => set(initialState),
  }))
)

// Selector hooks for common use cases
export const useIsConnected = () => useBroadcastStore((s) => s.isConnected)
export const useConnectionError = () => useBroadcastStore((s) => s.connectionError)
export const useMessages = (conversationId: string) =>
  useBroadcastStore((s) => s.getMessagesByConversation(conversationId))
